import React, { useState } from "react"

import { format } from "date-fns"
import { AlertCircle, ArrowLeft } from "lucide-react"
import { useNavigate } from "react-router-dom"

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { supabase } from "@/lib/supabase"

import DashboardLayout from "../layouts/DashboardLayout"

// Define the type for stage history items
interface StageHistoryItem {
  id: string
  application_id: string
  stage: string
  moved_at: string
}

const ApplicationStageHistoryDebug: React.FC = () => {
  const navigate = useNavigate()
  const [applicationId, setApplicationId] = useState<string>("")
  const [stageHistory, setStageHistory] = useState<StageHistoryItem[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [applicationDetails, setApplicationDetails] = useState<{
    id: string
    application_id: string
    current_stage: string
  } | null>(null)

  // Function to fetch stage history for a given application ID
  const fetchStageHistory = async () => {
    if (!applicationId) {
      setError("Please enter an application ID")
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // First, check if the application exists
      const { data: appData, error: appError } = await supabase
        .from("applications")
        .select("id, application_id,current_stage")
        .eq("id", applicationId)
        .single()

      if (appError) {
        throw new Error(`Application not found: ${appError.message}`)
      }

      setApplicationDetails(appData)

      // Then fetch the stage history
      const { data, error: historyError } = await supabase
        .from("application_stage_history")
        .select("*")
        .eq("application_id", applicationId)
        .order("moved_at", { ascending: true })

      if (historyError) {
        throw new Error(`Error fetching stage history: ${historyError.message}`)
      }

      setStageHistory(data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : "An unknown error occurred")
      setStageHistory([])
      setApplicationDetails(null)
    } finally {
      setIsLoading(false)
    }
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), "PPpp")
    } catch (_) {
      return dateString
    }
  }

  return (
    <DashboardLayout>
      <div className="flex items-center mb-6">
        <Button variant="ghost" onClick={() => navigate("/applications")} className="mr-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Applications
        </Button>
        <h1 className="text-2xl font-bold">Application Stage History Debug</h1>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Enter Application ID</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-2">
            <Input
              placeholder="Enter application UUID"
              value={applicationId}
              onChange={(e) => setApplicationId(e.target.value)}
              className="flex-1"
            />
            <Button onClick={fetchStageHistory} disabled={isLoading}>
              {isLoading ? "Loading..." : "Fetch History"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {applicationDetails && (
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Application Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Application ID</p>
                <p className="font-medium">{applicationDetails.application_id}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Current Stage</p>
                <p className="font-medium">{applicationDetails.current_stage}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Stage History</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : stageHistory.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Stage</TableHead>
                  <TableHead>Moved At</TableHead>
                  <TableHead>ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stageHistory.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.stage}</TableCell>
                    <TableCell>{formatDate(item.moved_at)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{item.id}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : applicationId && !isLoading ? (
            <p className="text-center py-4 text-muted-foreground">
              No stage history found for this application
            </p>
          ) : (
            <p className="text-center py-4 text-muted-foreground">
              Enter an application ID and click "Fetch History" to view the stage history
            </p>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  )
}

export default ApplicationStageHistoryDebug
